import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { supabase } from '../../supabase';
import { useAuth } from '../../context/AuthContext';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MonthlySpendingChart = () => {
  const { user } = useAuth();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [yearlyStats, setYearlyStats] = useState({
    totalSpent: 0,
    averageMonthly: 0,
    highestMonth: { month: '', amount: 0 },
    transactionCount: 0
  });

  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Generate available years (current year and 3 years back)
  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 3; i <= currentYear; i++) {
      years.push(i);
    }
    return years;
  };

  // Fetch monthly spending data
  const fetchMonthlySpending = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // Query bills for the selected year
      const { data: bills, error } = await supabase
        .from('bill_extracts')
        .select('total_amount, receipt_date, created_at')
        .eq('user_id', user.id)
        .gte('receipt_date', `${selectedYear}-01-01`)
        .lte('receipt_date', `${selectedYear}-12-31`)
        .order('receipt_date', { ascending: true });

      if (error) throw error;

      // Initialize monthly data
      const monthlyData = new Array(12).fill(0);
      const monthlyCount = new Array(12).fill(0);
      let totalSpent = 0;
      let transactionCount = 0;
      let highestAmount = 0;
      let highestMonthIndex = 0;

      // Process bills data
      bills.forEach(bill => {
        const amount = parseFloat(bill.total_amount) || 0;
        totalSpent += amount;
        transactionCount++;

        // Use receipt_date if available, otherwise fall back to created_at
        const dateStr = bill.receipt_date || bill.created_at;
        if (dateStr) {
          const date = new Date(dateStr);
          if (date.getFullYear() === selectedYear) {
            const monthIndex = date.getMonth();
            monthlyData[monthIndex] += amount;
            monthlyCount[monthIndex]++;

            // Track highest month
            if (monthlyData[monthIndex] > highestAmount) {
              highestAmount = monthlyData[monthIndex];
              highestMonthIndex = monthIndex;
            }
          }
        }
      });

      // Update chart data
      setChartData({
        labels: monthNames,
        datasets: [
          {
            label: `Monthly Spending ${selectedYear}`,
            data: monthlyData,
            backgroundColor: 'rgba(102, 126, 234, 0.8)',
            borderColor: 'rgb(102, 126, 234)',
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
            hoverBackgroundColor: 'rgba(102, 126, 234, 0.9)',
            hoverBorderColor: 'rgb(102, 126, 234)',
            hoverBorderWidth: 3,
          },
        ],
      });

      // Update yearly stats
      setYearlyStats({
        totalSpent: totalSpent,
        averageMonthly: totalSpent / 12,
        highestMonth: {
          month: monthNames[highestMonthIndex],
          amount: highestAmount
        },
        transactionCount: transactionCount
      });

    } catch (error) {
      console.error('Error fetching monthly spending:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlySpending();
  }, [selectedYear, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#333',
          font: {
            size: 14,
            weight: '600'
          }
        }
      },
      title: {
        display: true,
        text: `Monthly Spending Overview - ${selectedYear}`,
        color: '#333',
        font: {
          size: 18,
          weight: 'bold'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgb(102, 126, 234)',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return `Amount: $${context.raw.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#666',
          callback: function(value) {
            return '$' + value.toFixed(0);
          }
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index',
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // CSV Export function
  const exportToCSV = () => {
    if (!chartData) return;

    // Prepare CSV data
    const csvData = [
      ['Month', 'Amount ($)', 'Year'],
      ...chartData.labels.map((month, index) => [
        month,
        chartData.datasets[0].data[index].toFixed(2),
        selectedYear
      ])
    ];

    // Add summary statistics
    csvData.push([]);
    csvData.push(['Summary Statistics']);
    csvData.push(['Total Spent', yearlyStats.totalSpent.toFixed(2), selectedYear]);
    csvData.push(['Monthly Average', yearlyStats.averageMonthly.toFixed(2), selectedYear]);
    csvData.push(['Highest Month', `${yearlyStats.highestMonth.month} - $${yearlyStats.highestMonth.amount.toFixed(2)}`, selectedYear]);
    csvData.push(['Total Bills Count', yearlyStats.transactionCount, selectedYear]);

    // Convert to CSV string
    const csvContent = csvData.map(row => row.join(',')).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `monthly_spending_${selectedYear}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div style={{ 
      backgroundColor: '#fff', 
      padding: '25px', 
      borderRadius: '15px', 
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      margin: '20px 0'
    }}>
      {/* Header with Year Selector */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <h3 style={{ 
          color: '#333', 
          margin: 0, 
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          Monthly Spending Analytics
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <label htmlFor="year-select" style={{ 
            color: '#666', 
            fontWeight: '600',
            fontSize: '14px'
          }}>
            Year:
          </label>
          <select
            id="year-select"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{
              padding: '8px 15px',
              borderRadius: '8px',
              border: '2px solid #e1e5e9',
              backgroundColor: '#fff',
              color: '#333',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              outline: 'none',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgb(102, 126, 234)'}
            onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
          >
            {getAvailableYears().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button
            onClick={exportToCSV}
            disabled={!chartData || loading}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '2px solid rgb(102, 126, 234)',
              backgroundColor: !chartData || loading ? '#f8f9fa' : 'rgb(102, 126, 234)',
              color: !chartData || loading ? '#666' : '#fff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: !chartData || loading ? 'not-allowed' : 'pointer',
              outline: 'none',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
            onMouseEnter={(e) => {
              if (!loading && chartData) {
                e.target.style.backgroundColor = 'rgb(82, 106, 214)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && chartData) {
                e.target.style.backgroundColor = 'rgb(102, 126, 234)';
              }
            }}
          >
            📊 Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
        gap: '10px', 
        marginBottom: '20px' 
      }}>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '10px',
          borderRadius: '8px',
          textAlign: 'center',
          border: '1px solid #e9ecef',
          minHeight: '85px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'rgb(102, 126, 234)' }}>
            {formatCurrency(yearlyStats.totalSpent)}
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '3px' }}>
            Total Spent ({selectedYear})
          </div>
        </div>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '10px',
          borderRadius: '8px',
          textAlign: 'center',
          border: '1px solid #e9ecef',
          minHeight: '85px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
            {formatCurrency(yearlyStats.averageMonthly)}
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '3px' }}>
            Monthly Average
          </div>
        </div>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '10px',
          borderRadius: '8px',
          textAlign: 'center',
          border: '1px solid #e9ecef',
          minHeight: '85px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffc107' }}>
            {yearlyStats.highestMonth.month}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {formatCurrency(yearlyStats.highestMonth.amount)}
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
            Highest Month
          </div>
        </div>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '10px',
          borderRadius: '8px',
          textAlign: 'center',
          border: '1px solid #e9ecef',
          minHeight: '85px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#17a2b8' }}>
            {yearlyStats.transactionCount}
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '3px' }}>
            Total Bills
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height: '400px', marginBottom: '15px' }}>
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: '#666',
            fontSize: '16px'
          }}>
            Loading monthly spending data...
          </div>
        ) : chartData ? (
          <Bar data={chartData} options={chartOptions} />
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: '#666',
            fontSize: '16px',
            textAlign: 'center'
          }}>
            <div>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>📊</div>
              <div>No spending data found for {selectedYear}</div>
              <div style={{ fontSize: '14px', marginTop: '5px' }}>
                Upload some bills to see your monthly spending patterns
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div style={{ 
        fontSize: '12px', 
        color: '#666', 
        textAlign: 'center',
        borderTop: '1px solid #e9ecef',
        paddingTop: '15px'
      }}>
        Data based on bill receipt dates. Hover over chart points for detailed information.
      </div>
    </div>
  );
};

export default MonthlySpendingChart;
